import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Callout, Flex, Form, Heading, Text } from '@/components/common';
import { WizardScreen } from '@/components/layout';
import { ContinueButton, OnboardingWizardHeader } from '@/components/onboarding';
import { HeightField } from '@/components/profile/HeightField';
import { ProfileSchemaKey, useProfileSchema } from '@/lib/validators';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { fieldLabelStyle } from './styles';
import type { OnboardingStepProps } from './types';
import { useSaveAndNext } from './useSaveAndNext';

export function HeightStep({ flow }: OnboardingStepProps) {
  const { t } = useTranslation(['onboarding', 'profile']);
  const height = useOnboardingStore(state => state.height);
  const setHeight = useOnboardingStore(state => state.setHeight);
  const saveAndNext = useSaveAndNext(flow);
  const schema = useProfileSchema([ProfileSchemaKey.HEIGHT]);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { height: height != null && height > 0 ? height : undefined },
  });

  const onValid = async (data: z.infer<typeof schema>) => {
    setHeight(data.height);
    await saveAndNext();
  };

  return (
    <WizardScreen header={<OnboardingWizardHeader flow={flow} onSkip={flow.skip} />}>
      <Flex direction="column" gap="4" py="2" pb="5">
        <Heading size="5" weight="light">
          {t('steps.height.title')}{' '}
          <Text size="1" color="gray" as="span">
            {t('steps.height.optional')}
          </Text>
        </Heading>
        <Callout>{t('steps.height.callout')}</Callout>
        <Form.Root id="onboarding-height-form" onSubmit={handleSubmit(onValid)} noValidate>
          <Flex direction="column" gap="1">
            <Text size="1" color="gray" style={fieldLabelStyle}>
              {t('steps.height.fieldLabel')}
            </Text>
            <Controller
              name="height"
              control={control}
              render={({ field }) => (
                <HeightField
                  value={field.value}
                  onChange={field.onChange}
                  data-testid="onboarding-height"
                  error={errors.height?.message}
                />
              )}
            />
          </Flex>
        </Form.Root>
        <ContinueButton onClick={() => void handleSubmit(onValid)()}>
          {t('actions.continue')}
        </ContinueButton>
      </Flex>
    </WizardScreen>
  );
}
